import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import { LICHHOCLIENTUC_API, ACCOUNT_API } from '../../../../utils/apiUrl';
import { Post, Get } from '../../../../utils/httpProvider';

const CreateScheduleLienTucModal = ({
    show,
    onHide,
    lopId,
    lopDetail,
    studentsData = [],
    donViList = [],
    initialStudentId = null,
    initialDate = null,
    onSuccess
}) => {
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [selectedDonViList, setSelectedDonViList] = useState([]);
    const [donViKeyword, setDonViKeyword] = useState('');
    const [showDonViDropdown, setShowDonViDropdown] = useState(false);
    const [ngayBatDau, setNgayBatDau] = useState(moment().format('YYYY-MM-DD'));
    const [ngayKetThuc, setNgayKetThuc] = useState(moment().add(7, 'days').format('YYYY-MM-DD'));
    const [trinhDo, setTrinhDo] = useState('');
    const [ghiChu, setGhiChu] = useState('');
    const [studentSearch, setStudentSearch] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Multi-date selection mode: 'calendar' (trực quan trên lịch) | 'range' (dải ngày liên tục)
    const [dateSelectMode, setDateSelectMode] = useState('calendar');
    const [selectedDates, setSelectedDates] = useState([moment().format('YYYY-MM-DD')]);
    const [calendarMonth, setCalendarMonth] = useState(moment());

    // Reset or initialize state when opening modal
    React.useEffect(() => {
        if (show) {
            if (initialStudentId) {
                setSelectedStudentIds([initialStudentId]);
            } else {
                setSelectedStudentIds([]);
            }

            const targetDate = initialDate ? moment(initialDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
            setSelectedDates([targetDate]);
            setNgayBatDau(targetDate);
            setNgayKetThuc(targetDate);
            setCalendarMonth(moment(targetDate));

            setSelectedDonViList([]);
            setSelectedTutors([]);
            setTrinhDo('');
            setGhiChu('');
            setStudentSearch('');
        }
    }, [show, initialStudentId, initialDate]);

    const handleSelectDonVi = (dv) => {
        const id = dv.id || dv.Id || '00000000-0000-0000-0000-000000000000';
        const ten = dv.tenDonVi || dv.TenDonVi || '';
        if (!selectedDonViList.some(d => (d.id && d.id !== '00000000-0000-0000-0000-000000000000' && d.id === id) || d.tenDonVi.toLowerCase() === ten.toLowerCase())) {
            setSelectedDonViList(prev => [...prev, { id, tenDonVi: ten }]);
        }
        setDonViKeyword('');
        setShowDonViDropdown(false);
    };

    const handleRemoveDonVi = (targetName) => {
        setSelectedDonViList(prev => prev.filter(d => d.tenDonVi !== targetName && d.id !== targetName));
    };

    const handleAddCustomDonVi = () => {
        if (donViKeyword.trim()) {
            const customName = donViKeyword.trim();
            if (!selectedDonViList.some(d => d.tenDonVi.toLowerCase() === customName.toLowerCase())) {
                setSelectedDonViList(prev => [...prev, { id: '00000000-0000-0000-0000-000000000000', tenDonVi: customName }]);
            }
            setDonViKeyword('');
            setShowDonViDropdown(false);
        }
    };

    const handlePrevMonth = () => {
        setCalendarMonth(prev => moment(prev).subtract(1, 'month'));
    };

    const handleNextMonth = () => {
        setCalendarMonth(prev => moment(prev).add(1, 'month'));
    };

    const handleToggleDate = (dateStr) => {
        setSelectedDates(prev => {
            if (prev.includes(dateStr)) {
                return prev.filter(d => d !== dateStr);
            } else {
                return [...prev, dateStr].sort();
            }
        });
    };

    const handleClearAllDates = () => {
        setSelectedDates([]);
    };

    // Multi-select live search hospital-wide tutors
    const [selectedTutors, setSelectedTutors] = useState([]);
    const [tutorKeyword, setTutorKeyword] = useState('');
    const [tutorResults, setTutorResults] = useState([]);
    const [isSearchingTutor, setIsSearchingTutor] = useState(false);
    const [showTutorDropdown, setShowTutorDropdown] = useState(false);
    const searchTimeoutRef = useRef(null);

    const getTutorFullName = (item) => {
        const fName = item.firstName || item.accountAuthInfo?.firstName || '';
        const mName = item.middleName || item.accountAuthInfo?.middleName || '';
        const lName = item.lastName || item.accountAuthInfo?.lastName || '';
        let name = `${lName} ${mName} ${fName}`.replace(/\s+/g, ' ').trim();
        if (!name) name = item.fullName || item.fullname || item.userName || 'Chưa rõ tên';
        return name;
    };

    const handleTutorSearchChange = (e) => {
        const val = e.target.value;
        setTutorKeyword(val);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (!val || val.trim().length < 2) {
            setTutorResults([]);
            setShowTutorDropdown(false);
            setIsSearchingTutor(false);
            return;
        }

        setIsSearchingTutor(true);
        setShowTutorDropdown(true);

        searchTimeoutRef.current = setTimeout(async () => {
            try {
                const response = await Get(ACCOUNT_API.GET_PAGING_ACCOUNT_NOT_MENU, null, {
                    Keyword: val.trim(),
                    PageIndex: 1,
                    PageSize: 15
                });
                const resData = response?.data || response;
                const items = resData?.data || resData?.$values || (Array.isArray(resData) ? resData : []);
                setTutorResults(items);
            } catch (err) {
                console.error("Lỗi tìm kiếm cán bộ/giảng viên:", err);
                setTutorResults([]);
            } finally {
                setIsSearchingTutor(false);
            }
        }, 300);
    };

    const handleSelectTutor = (item) => {
        const id = item.id || item.accountId;
        if (selectedTutors.some(t => t.id === id)) {
            toast.info('Giảng viên này đã được chọn trong danh sách.');
            setTutorKeyword('');
            setShowTutorDropdown(false);
            return;
        }

        const fullName = getTutorFullName(item);
        const cccd = item.identityCardNumber || item.identityCard || item.accountAuthInfo?.identityCardNumber || item.accountAuthInfo?.identityCard || '';

        setSelectedTutors(prev => [
            ...prev,
            {
                id: id,
                fullName: fullName,
                identityCard: cccd,
                donVi: item.donVi || item.accountAuthInfo?.donVi || ''
            }
        ]);

        setTutorKeyword('');
        setTutorResults([]);
        setShowTutorDropdown(false);
    };

    const handleRemoveTutor = (tutorId) => {
        setSelectedTutors(prev => prev.filter(t => t.id !== tutorId));
    };

    const handleSelectAllStudents = (e) => {
        if (e.target.checked) {
            setSelectedStudentIds(studentsData.map(s => s.id));
        } else {
            setSelectedStudentIds([]);
        }
    };

    const handleToggleStudent = (sId) => {
        setSelectedStudentIds(prev =>
            prev.includes(sId) ? prev.filter(id => id !== sId) : [...prev, sId]
        );
    };

    const filteredStudents = studentsData.filter(s => {
        if (!studentSearch) return true;
        const key = studentSearch.toLowerCase().trim();
        return (
            (s.hoTen && s.hoTen.toLowerCase().includes(key)) ||
            (s.soCccd && s.soCccd.includes(key))
        );
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedStudentIds.length === 0) {
            toast.warning('Vui lòng chọn ít nhất một học viên để phân lịch.');
            return;
        }
        if (dateSelectMode === 'calendar') {
            if (selectedDates.length === 0) {
                toast.warning('Vui lòng chọn ít nhất một ngày học trên lịch.');
                return;
            }
        } else {
            if (!ngayBatDau) {
                toast.warning('Vui lòng chọn ngày bắt đầu.');
                return;
            }
        }

        const sortedDates = [...selectedDates].sort();
        const primaryDonViId = lopDetail?.khoaDeXuatId || '00000000-0000-0000-0000-000000000000';
        const donViText = lopDetail?.khoaDeXuatTen || 'Khoa chuyên môn';

        const payload = {
            lopDaoTaoLienTucId: lopId,
            hoSoTuyenSinhIds: selectedStudentIds,
            donViId: primaryDonViId,
            donVi: donViText,
            donVis: [{
                id: primaryDonViId,
                tenDonVi: donViText
            }],
            giangVienLienTucIds: selectedTutors.map(t => t.id),
            giangViens: selectedTutors.map((t) => ({
                id: t.id,
                ten: t.fullName,
                laGiangVienChinh: false
            })),
            giangVienTen: selectedTutors.map(t => t.fullName).join(', '),
            ngayBatDau: dateSelectMode === 'calendar' ? sortedDates[0] : ngayBatDau,
            ngayKetThuc: dateSelectMode === 'calendar' ? sortedDates[sortedDates.length - 1] : (ngayKetThuc || null),
            selectedDates: dateSelectMode === 'calendar' ? sortedDates : null,
            trinhDo: trinhDo,
            ghiChu: ghiChu,
            trangThai: 1
        };

        try {
            setIsSubmitting(true);
            const res = await Post(LICHHOCLIENTUC_API.CREATE_ITEM, null, payload);
            if (res) {
                toast.success(`Đã phân lịch thành công cho ${selectedStudentIds.length} học viên.`);
                setSelectedStudentIds([]);
                setSelectedTutors([]);
                setSelectedDonViList([]);
                setSelectedDates([moment().format('YYYY-MM-DD')]);
                setGhiChu('');
                setTrinhDo('');
                if (onSuccess) onSuccess();
                onHide();
            }
        } catch (err) {
            toast.error(err?.message || 'Không thể tạo lịch học.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderCalendar = () => {
        const startOfMonth = moment(calendarMonth).startOf('month');
        const daysInMonth = calendarMonth.daysInMonth();
        // ISO weekday: 1 = Thứ 2, ..., 7 = Chủ nhật
        const startDayOfWeek = startOfMonth.isoWeekday();

        const blanks = [];
        for (let i = 1; i < startDayOfWeek; i++) {
            blanks.push(<div key={`blank-${i}`} style={{ width: '14.28%', height: '32px' }}></div>);
        }

        const days = [];
        const todayStr = moment().format('YYYY-MM-DD');

        for (let d = 1; d <= daysInMonth; d++) {
            const dateObj = moment(calendarMonth).date(d);
            const dateStr = dateObj.format('YYYY-MM-DD');
            const isSelected = selectedDates.includes(dateStr);
            const isToday = dateStr === todayStr;
            const isWeekend = dateObj.isoWeekday() >= 6;

            days.push(
                <div
                    key={dateStr}
                    style={{
                        width: '14.28%',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        padding: '1px'
                    }}
                    onClick={() => handleToggleDate(dateStr)}
                >
                    <div
                        style={{
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: isSelected ? '700' : (isToday ? '600' : 'normal'),
                            backgroundColor: isSelected ? '#007bff' : (isToday ? '#e8f0fe' : 'transparent'),
                            color: isSelected ? '#ffffff' : (isWeekend ? '#e03131' : '#333333'),
                            border: isToday && !isSelected ? '1px dashed #007bff' : '1px solid transparent',
                            boxShadow: isSelected ? '0 2px 4px rgba(0, 123, 255, 0.4)' : 'none',
                            transition: 'all 0.12s ease'
                        }}
                        title={dateObj.format('dddd, DD/MM/YYYY')}
                        onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = '#f1f3f5';
                        }}
                        onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.backgroundColor = isToday ? '#e8f0fe' : 'transparent';
                        }}
                    >
                        {d}
                    </div>
                </div>
            );
        }

        return (
            <div className="border rounded p-2 bg-white">
                {/* Header điều hướng Tháng / Năm */}
                <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="py-0 px-2 font-weight-bold"
                        onClick={handlePrevMonth}
                        type="button"
                    >
                        <i className="fas fa-chevron-left"></i>
                    </Button>
                    <span className="font-weight-bold text-primary" style={{ fontSize: '13px' }}>
                        <i className="far fa-calendar-alt mr-1"></i>
                        Tháng {calendarMonth.format('MM / YYYY')}
                    </span>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="py-0 px-2 font-weight-bold"
                        onClick={handleNextMonth}
                        type="button"
                    >
                        <i className="fas fa-chevron-right"></i>
                    </Button>
                </div>

                {/* Thứ trong tuần T2 - CN */}
                <div className="d-flex text-center font-weight-bold text-muted mb-1" style={{ fontSize: '11px', borderBottom: '1px solid #dee2e6', paddingBottom: '3px' }}>
                    <div style={{ width: '14.28%' }}>T2</div>
                    <div style={{ width: '14.28%' }}>T3</div>
                    <div style={{ width: '14.28%' }}>T4</div>
                    <div style={{ width: '14.28%' }}>T5</div>
                    <div style={{ width: '14.28%' }}>T6</div>
                    <div style={{ width: '14.28%', color: '#e03131' }}>T7</div>
                    <div style={{ width: '14.28%', color: '#e03131' }}>CN</div>
                </div>

                {/* Lưới các ngày trong tháng */}
                <div className="d-flex flex-wrap">
                    {blanks}
                    {days}
                </div>

                {selectedDates.length > 0 && (
                    <div className="d-flex justify-content-end align-items-center mt-2 pt-2 border-top">
                        <Button
                            variant="link"
                            size="sm"
                            type="button"
                            className="text-danger py-0 px-1 text-decoration-none"
                            style={{ fontSize: '11px' }}
                            onClick={handleClearAllDates}
                        >
                            <i className="fas fa-trash-alt mr-1"></i>Xóa tất cả ngày đã chọn
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="font-weight-bold text-primary">
                    <i className="fas fa-calendar-plus mr-2"></i>
                    Phân Lịch Học Thủ Công (Đào Tạo Liên Tục)
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body className="py-3">
                    <Row>
                        {/* Cột chọn học viên */}
                        <Col md={6} className="border-right">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="font-weight-bold mb-0 text-dark">
                                    Chọn Học Viên <span className="text-danger">*</span>
                                    <Badge variant="primary" className="ml-2">
                                        Đã chọn {selectedStudentIds.length}/{studentsData.length}
                                    </Badge>
                                </Form.Label>
                                {studentsData.length > 0 && (
                                    <Form.Check
                                        type="checkbox"
                                        id="check-all-students"
                                        label="Chọn tất cả"
                                        checked={selectedStudentIds.length === studentsData.length && studentsData.length > 0}
                                        onChange={handleSelectAllStudents}
                                        className="small font-weight-bold"
                                    />
                                )}
                            </div>

                            <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Tìm tên hoặc CCCD học viên..."
                                className="mb-2"
                                value={studentSearch}
                                onChange={(e) => setStudentSearch(e.target.value)}
                            />

                            <div className="border rounded p-2 bg-white" style={{ maxHeight: '480px', overflowY: 'auto' }}>
                                {filteredStudents.length === 0 ? (
                                    <div className="text-muted text-center py-4 small">
                                        Không tìm thấy học viên trong lớp
                                    </div>
                                ) : (
                                    filteredStudents.map((s, idx) => {
                                        const isChecked = selectedStudentIds.includes(s.id);
                                        return (
                                            <div
                                                key={s.id || idx}
                                                className={`p-2 border-bottom d-flex align-items-center justify-content-between ${isChecked ? 'bg-light' : ''}`}
                                                style={{ cursor: 'pointer', borderRadius: '4px' }}
                                                onClick={() => handleToggleStudent(s.id)}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`student-check-${s.id}`}
                                                        checked={isChecked}
                                                        onChange={() => {}}
                                                        className="mr-2"
                                                    />
                                                    <div>
                                                        <div className="font-weight-bold text-dark small">{s.hoTen}</div>
                                                        <div className="text-muted" style={{ fontSize: '11px' }}>
                                                            {s.soCccd ? `CCCD: ${s.soCccd}` : (s.soDienThoai ? `SĐT: ${s.soDienThoai}` : '')}
                                                        </div>
                                                    </div>
                                                </div>
                                                {isChecked && <i className="fas fa-check-circle text-primary"></i>}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </Col>

                        {/* Cột thông tin phân lịch */}
                        <Col md={6}>
                            {/* KHOA PHỤ TRÁCH ĐÀO TẠO (CỐ ĐỊNH THEO LỚP) */}
                            <div className="p-3 rounded mb-3 border shadow-xs" style={{ backgroundColor: '#f0f9ff', borderColor: '#bae6fd' }}>
                                <div className="d-flex align-items-center">
                                    <div className="p-2 rounded bg-white mr-3 shadow-xs text-primary">
                                        <i className="fas fa-hospital-alt fa-lg"></i>
                                    </div>
                                    <div>
                                        <div className="text-muted small font-weight-bold text-uppercase">Khoa phụ trách đào tạo</div>
                                        <div className="font-weight-bold text-dark" style={{ fontSize: '15px' }}>
                                            {lopDetail?.khoaDeXuatTen || "Khoa chuyên môn"}
                                        </div>
                                        <small className="text-secondary">Cố định theo lớp đào tạo liên tục</small>
                                    </div>
                                </div>
                            </div>

                            {/* CHỌN NHIỀU GIẢNG VIÊN TOÀN BỆNH VIỆN */}
                            <Form.Group className="mb-3 position-relative">
                                <Form.Label className="font-weight-bold small text-dark d-flex justify-content-between align-items-center mb-1">
                                    <span>
                                        <i className="fas fa-user-md text-primary mr-1"></i>
                                        Giảng viên phụ trách (Toàn bệnh viện)
                                    </span>
                                    <Badge variant={selectedTutors.length > 0 ? "success" : "secondary"}>
                                        Đã chọn {selectedTutors.length}
                                    </Badge>
                                </Form.Label>

                                {/* Danh sách các giảng viên đã chọn dạng thẻ badge */}
                                {selectedTutors.length > 0 && (
                                    <div className="d-flex flex-wrap mb-2 p-2 rounded bg-light border" style={{ gap: '6px' }}>
                                        {selectedTutors.map((t, idx) => (
                                            <Badge
                                                key={t.id || idx}
                                                variant="primary"
                                                className="p-2 d-inline-flex align-items-center shadow-xs"
                                                style={{ fontSize: '12px', fontWeight: '500' }}
                                            >
                                                <i className="fas fa-chalkboard-teacher mr-1"></i>
                                                {t.fullName}
                                                {t.identityCard && <small className="ml-1 opacity-75">({t.identityCard})</small>}
                                                <i
                                                    className="fas fa-times-circle ml-2"
                                                    style={{ cursor: 'pointer', fontSize: '13px' }}
                                                    onClick={() => handleRemoveTutor(t.id)}
                                                    title="Bỏ chọn giảng viên này"
                                                ></i>
                                            </Badge>
                                        ))}
                                    </div>
                                )}

                                {/* Ô tìm kiếm để thêm tiếp giảng viên */}
                                <div className="position-relative">
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder="Gõ tên hoặc CCCD để tìm và thêm giảng viên..."
                                        value={tutorKeyword}
                                        onChange={handleTutorSearchChange}
                                        autoComplete="off"
                                    />
                                    {isSearchingTutor && (
                                        <div className="position-absolute" style={{ right: 10, top: 7 }}>
                                            <Spinner animation="border" size="sm" variant="primary" />
                                        </div>
                                    )}

                                    {/* Dropdown gợi ý kết quả tìm kiếm */}
                                    {showTutorDropdown && (
                                        <div
                                            className="position-absolute w-100 bg-white border rounded shadow-lg mt-1"
                                            style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1050 }}
                                        >
                                            {tutorResults.length === 0 ? (
                                                <div className="p-3 text-center text-muted small">
                                                    {isSearchingTutor ? 'Đang tra cứu hệ thống...' : 'Không tìm thấy cán bộ / giảng viên nào khớp từ khóa'}
                                                </div>
                                            ) : (
                                                tutorResults.map((item, idx) => {
                                                    const name = getTutorFullName(item);
                                                    const cccd = item.identityCardNumber || item.identityCard || item.accountAuthInfo?.identityCardNumber || item.accountAuthInfo?.identityCard || '';
                                                    const uName = item.userName || item.username || '';
                                                    const isAlreadyChosen = selectedTutors.some(t => t.id === (item.id || item.accountId));

                                                    return (
                                                        <div
                                                            key={item.id || idx}
                                                            className={`p-2 border-bottom d-flex align-items-center justify-content-between ${isAlreadyChosen ? 'bg-light text-muted' : ''}`}
                                                            style={{ cursor: isAlreadyChosen ? 'default' : 'pointer' }}
                                                            onClick={() => !isAlreadyChosen && handleSelectTutor(item)}
                                                            onMouseEnter={(e) => { if (!isAlreadyChosen) e.currentTarget.style.backgroundColor = '#f0f4f8'; }}
                                                            onMouseLeave={(e) => { if (!isAlreadyChosen) e.currentTarget.style.backgroundColor = '#fff'; }}
                                                        >
                                                            <div>
                                                                <div className="font-weight-bold small">{name}</div>
                                                                <div className="text-muted" style={{ fontSize: '11px' }}>
                                                                    {cccd ? `CCCD: ${cccd}` : (uName ? `Username: ${uName}` : '')}
                                                                </div>
                                                            </div>
                                                            {isAlreadyChosen ? (
                                                                <span className="badge badge-secondary small">Đã thêm</span>
                                                            ) : (
                                                                <Button variant="outline-primary" size="sm" className="py-0 px-2 small">
                                                                    + Thêm
                                                                </Button>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Form.Group>

                            {/* KHU VỰC THỜI GIAN ĐÀO TẠO (CHỌN TRỰC QUAN TRÊN LỊCH HOẶC DẢI NGÀY) */}
                            <Form.Group className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <Form.Label className="font-weight-bold small text-dark mb-0">
                                        <i className="fas fa-calendar-day text-primary mr-1"></i>
                                        Thời gian học <span className="text-danger">*</span>
                                        {dateSelectMode === 'calendar' && (
                                            <Badge variant={selectedDates.length > 0 ? "primary" : "secondary"} className="ml-2 font-weight-normal" style={{ fontSize: '11px' }}>
                                                Đã chọn {selectedDates.length} ngày
                                            </Badge>
                                        )}
                                    </Form.Label>
                                    <div className="btn-group btn-group-sm">
                                        <Button
                                            variant={dateSelectMode === 'calendar' ? 'primary' : 'outline-secondary'}
                                            size="sm"
                                            className="py-0 px-2 font-weight-bold"
                                            style={{ fontSize: '11px' }}
                                            onClick={() => setDateSelectMode('calendar')}
                                            type="button"
                                        >
                                            <i className="fas fa-calendar-alt mr-1"></i>
                                            Chọn trên lịch
                                        </Button>
                                        <Button
                                            variant={dateSelectMode === 'range' ? 'primary' : 'outline-secondary'}
                                            size="sm"
                                            className="py-0 px-2 font-weight-bold"
                                            style={{ fontSize: '11px' }}
                                            onClick={() => setDateSelectMode('range')}
                                            type="button"
                                        >
                                            <i className="fas fa-arrows-alt-h mr-1"></i>
                                            Dải ngày liên tục
                                        </Button>
                                    </div>
                                </div>

                                {dateSelectMode === 'calendar' ? (
                                    renderCalendar()
                                ) : (
                                    <Row className="mt-2">
                                        <Col md={6}>
                                            <Form.Label className="font-weight-bold small text-dark">
                                                Từ ngày <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                size="sm"
                                                value={ngayBatDau}
                                                onChange={(e) => setNgayBatDau(e.target.value)}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <Form.Label className="font-weight-bold small text-dark">
                                                Đến ngày
                                            </Form.Label>
                                            <Form.Control
                                                type="date"
                                                size="sm"
                                                value={ngayKetThuc}
                                                onChange={(e) => setNgayKetThuc(e.target.value)}
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="font-weight-bold small text-dark">
                                    Nội dung đào tạo / Trình độ
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="VD: Tuần 1 - Lý thuyết lâm sàng..."
                                    value={trinhDo}
                                    onChange={(e) => setTrinhDo(e.target.value)}
                                />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label className="font-weight-bold small text-dark">
                                    Ghi chú bổ sung
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    size="sm"
                                    placeholder="Ghi chú phân công, địa điểm học..."
                                    value={ghiChu}
                                    onChange={(e) => setGhiChu(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="bg-light py-2">
                    <Button variant="secondary" size="sm" onClick={onHide} disabled={isSubmitting}>
                        Hủy
                    </Button>
                    <Button variant="primary" size="sm" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                Đang lưu...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save mr-1"></i> Lưu Phân Lịch
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CreateScheduleLienTucModal;
